import { formatShortDate } from '../../../../ts-utils/date-utils'

/** How a single stored value reads back when its field is not being edited. */
export type FieldValueKind =
  | 'text'
  | 'number'
  | 'select'
  | 'textarea'
  | 'switch'
  | 'date'
  | 'phone'

export interface FormatFieldValueOptions {
  /** Select options — the stored value reads as its option's label. */
  options?: ReadonlyArray<{ label: string; value: string }>
  /** Shown after the value (e.g. "lbs"). */
  unit?: string
}

const trimmed = (value: unknown): string =>
  value === null || value === undefined ? '' : String(value).trim()

/**
 * The read-mode text for one stored value, or `null` when it has none (the view
 * renders its own empty marker).
 *
 * - switch → "Yes" / "No" (a switch always has a value)
 * - select → the option's label, falling back to the stored value
 * - date   → "Jan 1, 2024" (`YYYY-MM-DD` is a calendar date, so no zone applies)
 * - with a `unit` → "172 lbs"
 */
export const formatFieldValue = (
  kind: FieldValueKind,
  raw: unknown,
  { options, unit }: FormatFieldValueOptions = {}
): string | null => {
  if (kind === 'switch') {
    return raw ? 'Yes' : 'No'
  }

  const value = trimmed(raw)
  if (!value) {
    return null
  }

  if (kind === 'select') {
    return options?.find((option) => option.value === value)?.label ?? value
  }

  if (kind === 'date') {
    return formatShortDate(value)
  }

  return unit ? `${value} ${unit}` : value
}
